import React from 'react';
import { useTweaks, TweaksPanel, TweakSection, TweakRadio } from './components/tweaks-panel.jsx';
import LandingPage from './pages/landing.jsx';
import LoginPage from './pages/login.jsx';
import DashboardPage from './pages/dashboard.jsx';

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "fontFamily": "manrope",
  "heroVariant": "editorial"
}/*EDITMODE-END*/;

// Hashes starting with "#/" are routes; anything else ("#how", "#faq") is an
// in-page anchor on the landing and must not reset the route or scroll to top.
const isRouteHash = (h) => !h || h.startsWith('#/');

function scrollToAnchor(hash, behavior = 'smooth') {
  const el = document.getElementById(hash.slice(1));
  if (el) el.scrollIntoView({ behavior });
}

export default function App() {
  const [route, setRoute] = React.useState(() => {
    const h = window.location.hash;
    return isRouteHash(h) ? (h || '#/') : '#/';
  });
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);

  React.useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      if (isRouteHash(h)) {
        setRoute(h || '#/');
        window.scrollTo(0, 0);
      } else {
        setRoute(r => (r.startsWith('#/login') || r.startsWith('#/dashboard')) ? '#/' : r);
        requestAnimationFrame(() => scrollToAnchor(h));
      }
    };
    // Clicking the anchor that is already in the URL fires no hashchange — scroll anyway.
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      const h = a && a.getAttribute('href');
      if (h && h.length > 1 && !isRouteHash(h) && h === window.location.hash) {
        e.preventDefault();
        scrollToAnchor(h);
      }
    };
    window.addEventListener('hashchange', onHash);
    document.addEventListener('click', onClick);
    // Deep link like autopacksafe.com/#coverage — scroll once the page has rendered,
    // and again on `load`, because the browser resets scroll when loading finishes.
    const scrollInitial = () => {
      if (!isRouteHash(window.location.hash)) scrollToAnchor(window.location.hash, 'auto');
    };
    requestAnimationFrame(scrollInitial);
    if (document.readyState !== 'complete') window.addEventListener('load', scrollInitial, { once: true });
    return () => {
      window.removeEventListener('hashchange', onHash);
      document.removeEventListener('click', onClick);
      window.removeEventListener('load', scrollInitial);
    };
  }, []);

  React.useEffect(() => {
    document.body.dataset.font = t.fontFamily || 'geist';
  }, [t.fontFamily]);

  let page;
  if (route.startsWith('#/login')) page = <LoginPage />;
  else if (route.startsWith('#/dashboard')) page = <DashboardPage />;
  else page = <LandingPage heroVariant={t.heroVariant} />;

  const isLanding = !route.startsWith('#/login') && !route.startsWith('#/dashboard');

  return (
    <>
      {page}
      <TweaksPanel title="Tweaks">
        <TweakSection label="Tipografía" />
        <TweakRadio
          label="Familia"
          value={t.fontFamily}
          options={['geist', 'inter', 'manrope']}
          onChange={v => setTweak('fontFamily', v)}
        />
        {isLanding && (
          <>
            <TweakSection label="Hero (landing)" />
            <TweakRadio
              label="Variante"
              value={t.heroVariant}
              options={['editorial', 'centered']}
              onChange={v => setTweak('heroVariant', v)}
            />
          </>
        )}
        <TweakSection label="Atajos" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <a href="#/" className="btn btn-secondary btn-sm">Landing</a>
          <a href="#/login" className="btn btn-secondary btn-sm">Login</a>
          <a href="#/dashboard" className="btn btn-secondary btn-sm">Dashboard</a>
        </div>
      </TweaksPanel>
    </>
  );
}
